import React, { useEffect, useState } from 'react';
import {
  Search,
  AlertCircle,
  CheckCircle2,
  Copy,
  ExternalLink,
  User,
  Car,
  MapPin,
  Phone,
  Mail,
  CreditCard,
  Wallet,
  ClipboardEdit,
  ListChecks,
  Tag,
  Loader2,
  XCircle,
  ShieldAlert,
  IdCard,
} from 'lucide-react';
import { detectarTipoBusca } from '../utils/atendimento';

const API_URL = '/api';

function formatarData(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function tempoComoAssociado(iso) {
  if (!iso) return '';
  const inicio = new Date(iso);
  if (isNaN(inicio.getTime())) return '';
  const agora = new Date();
  let meses = (agora.getFullYear() - inicio.getFullYear()) * 12 + (agora.getMonth() - inicio.getMonth());
  if (agora.getDate() < inicio.getDate()) meses -= 1;
  if (meses < 1) return 'menos de 1 mês';
  if (meses < 12) return `há ${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  const anos = Math.floor(meses / 12);
  const mesesResto = meses % 12;
  if (mesesResto === 0) return `há ${anos} ${anos === 1 ? 'ano' : 'anos'}`;
  return `há ${anos}a ${mesesResto}m`;
}

function formatarMoeda(valor) {
  if (valor == null || isNaN(valor)) return '';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function SecaoForm({ titulo, icon: Icon, children }) {
  return (
    <fieldset className="border border-line/70 rounded-2xl p-5">
      <legend className="px-2 flex items-center gap-2 text-xs font-semibold text-ink-muted uppercase tracking-wider">
        {Icon && <Icon size={13} />}
        {titulo}
      </legend>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {children}
      </div>
    </fieldset>
  );
}

function CampoLabel({ label, children, fullWidth }) {
  return (
    <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

export default function NovoAtendimento({
  cpfBusca,
  setCpfBusca,
  buscarAssociado,
  associado,
  situacaoFinanceira,
  boletosPendentes,
  carregando,
  erro,
  podeOperarAtendimento,
  apiFetch,
}) {
  const [copiado, setCopiado] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [salvandoAtendimento, setSalvandoAtendimento] = useState(false);
  const [erroAtendimento, setErroAtendimento] = useState(null);
  const [mensagemAtendimento, setMensagemAtendimento] = useState(null);

  const [tipoAtendimento, setTipoAtendimento] = useState('colisao');
  const [prioridadeAtendimento, setPrioridadeAtendimento] = useState('alta');
  const [veiculoSelecionadoId, setVeiculoSelecionadoId] = useState('');

  const [nomeComunicante, setNomeComunicante] = useState('');
  const [contatoComunicante, setContatoComunicante] = useState('');
  const [comunicanteCondutor, setComunicanteCondutor] = useState('Sim');
  const [cnhEmPosse, setCnhEmPosse] = useState('Sim');
  const [crlvEmPosse, setCrlvEmPosse] = useState('Sim');

  const [passageiros, setPassageiros] = useState('1');
  const [idososCriancasPcd, setIdososCriancasPcd] = useState('Não');
  const [horarioFato, setHorarioFato] = useState('');
  const [localFato, setLocalFato] = useState('');
  const [envolveuOutrosVeiculos, setEnvolveuOutrosVeiculos] = useState('0');
  const [quemCausou, setQuemCausou] = useState('Terceiro');
  const [consumoAlcoolMedicamentos, setConsumoAlcoolMedicamentos] = useState('Não');
  const [rodasTravadas, setRodasTravadas] = useState('Não');
  const [cargaSobrepeso, setCargaSobrepeso] = useState('Não');

  const [relatoAtendimento, setRelatoAtendimento] = useState('');
  const [pendenciasAtendimento, setPendenciasAtendimento] = useState(
    'Aguardando fotos do veículo, CNH, CRLV e E-BRAT'
  );

  const copiarPix = (codigo, index) => {
    navigator.clipboard.writeText(codigo);
    setCopiado(index);
    setTimeout(() => setCopiado(null), 2000);
  };

  useEffect(() => {
    setMostrarFormulario(false);
    setErroAtendimento(null);
    setMensagemAtendimento(null);

    if (associado) {
      setNomeComunicante('');
      setContatoComunicante(associado.telefone || '');
      setComunicanteCondutor('Sim');
      setCnhEmPosse('Sim');
      setCrlvEmPosse('Sim');
      setTipoAtendimento('colisao');
      setPrioridadeAtendimento('alta');
      setPassageiros('1');
      setIdososCriancasPcd('Não');
      setHorarioFato('');
      setLocalFato('');
      setEnvolveuOutrosVeiculos('0');
      setQuemCausou('Terceiro');
      setConsumoAlcoolMedicamentos('Não');
      setRodasTravadas('Não');
      setCargaSobrepeso('Não');
      setRelatoAtendimento('');
      setPendenciasAtendimento('Aguardando fotos do veículo, CNH, CRLV e E-BRAT');

      if (Array.isArray(associado.veiculos) && associado.veiculos.length > 0) {
        setVeiculoSelecionadoId(String(associado.veiculos[0].id));
      } else {
        setVeiculoSelecionadoId('');
      }
    }
  }, [associado]);

  const veiculoSelecionado =
    associado?.veiculos?.find((v) => String(v.id) === String(veiculoSelecionadoId)) ||
    associado?.veiculos?.[0] ||
    null;

  const abrirAtendimento = async (e) => {
    e.preventDefault();
    try {
      setSalvandoAtendimento(true);
      setErroAtendimento(null);
      setMensagemAtendimento(null);

      if (!associado) throw new Error('Busque um associado antes de abrir atendimento.');
      if (!localFato.trim()) throw new Error('Informe o local do fato.');
      if (!relatoAtendimento.trim()) throw new Error('Informe o relato do atendimento.');

      const associadoIdFinal = associado.idLocal || associado.id;

      const payload = {
        associadoId: associadoIdFinal,
        localizacao: localFato.trim(),
        problemaRelato: relatoAtendimento.trim(),
        prioridade: prioridadeAtendimento,
        tipoAtendimento,
        placaAtendimento: veiculoSelecionado?.placa || '',
        modeloVeiculoAtendimento: veiculoSelecionado?.modelo || '',
        dadosComplementares: {
          nomeComunicante,
          contato: contatoComunicante,
          comunicanteCondutor,
          cnhEmPosse,
          crlvEmPosse,
          passageiros: Number(passageiros || 0),
          idososCriancasPcd,
          horarioFato,
          envolveuOutrosVeiculos: Number(envolveuOutrosVeiculos || 0),
          quemCausou,
          consumoAlcoolMedicamentos,
          rodasTravadas,
          cargaSobrepeso,
          pendencias: pendenciasAtendimento,
        },
      };

      const response = await apiFetch(`${API_URL}/atendimentos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao abrir atendimento.');

      setMensagemAtendimento(`Atendimento aberto com sucesso. ID: ${data.id}`);
      setMostrarFormulario(false);
    } catch (err) {
      setErroAtendimento(err.message || 'Erro ao abrir atendimento.');
    } finally {
      setSalvandoAtendimento(false);
    }
  };

  const etapaAtual = associado ? '2. Abrir atendimento' : '1. Buscar associado';

  return (
    <div className="flex flex-col gap-6">
      {/* Hero */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Novo Atendimento</h1>
          <p className="text-sm text-ink-subtle mt-1">
            Busque o associado, valide a situação e registre a ocorrência.
          </p>
        </div>
        <div className="chip-brand !px-3 !py-1.5">
          <span className="text-[11px] uppercase tracking-wider opacity-70">Etapa</span>
          <span className="font-semibold">{etapaAtual}</span>
        </div>
      </div>

      {/* Busca por CPF ou placa */}
      <section className="card p-5">
        <header className="flex items-center gap-2 mb-3">
          <Search size={16} className="text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink">Buscar associado</h2>
        </header>

        {(() => {
          const deteccao = detectarTipoBusca(cpfBusca);
          const IconeAtual =
            deteccao.tipo === 'cpf' ? IdCard :
            deteccao.tipo === 'placa' ? Car :
            Search;
          const dicaTipo =
            deteccao.tipo === 'cpf' ? 'CPF detectado' :
            deteccao.tipo === 'placa' ? 'Placa detectada' :
            null;

          return (
            <>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <IconeAtual
                    size={16}
                    className={[
                      'absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors',
                      deteccao.tipo === 'cpf' ? 'text-brand-600' :
                      deteccao.tipo === 'placa' ? 'text-info-600' :
                      'text-ink-faint',
                    ].join(' ')}
                  />
                  <input
                    type="text"
                    placeholder="CPF (somente números) ou placa do veículo"
                    className="input pl-9 uppercase"
                    value={cpfBusca}
                    onChange={(e) => setCpfBusca(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && buscarAssociado()}
                  />
                </div>
                <button
                  className="btn-primary"
                  onClick={buscarAssociado}
                  disabled={carregando || !podeOperarAtendimento}
                >
                  {carregando ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                  {!podeOperarAtendimento ? 'Sem permissão' : carregando ? 'Buscando…' : 'Buscar'}
                </button>
              </div>

              <p className="text-xs text-ink-subtle mt-2 flex items-center gap-1.5 min-h-[16px]">
                {dicaTipo ? (
                  <>
                    <CheckCircle2 size={12} className={deteccao.tipo === 'cpf' ? 'text-brand-600' : 'text-info-600'} />
                    <span>{dicaTipo}</span>
                  </>
                ) : (
                  <span className="text-ink-faint">Digite 11 dígitos (CPF) ou 6-8 caracteres (placa)</span>
                )}
              </p>
            </>
          );
        })()}

        {erro && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-danger-50 text-danger-700 px-3.5 py-2.5 text-sm">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{erro}</span>
          </div>
        )}
        {!podeOperarAtendimento && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-warning-50 text-warning-700 px-3.5 py-2.5 text-sm">
            <ShieldAlert size={16} className="mt-0.5 shrink-0" />
            <span>Seu perfil não tem permissão para operar atendimentos.</span>
          </div>
        )}
      </section>

      {/* Alerta financeiro */}
      {boletosPendentes && boletosPendentes.resumo.quantidade > 0 && (
        <section className={`card p-5 border-l-4 ${situacaoFinanceira?.inadimplente ? 'border-danger-500' : 'border-info-500'}`}>
          <header className="flex items-center gap-2 mb-4">
            <Wallet size={16} className={situacaoFinanceira?.inadimplente ? 'text-danger-600' : 'text-info-600'} />
            <h2 className="text-sm font-semibold text-ink">
              {situacaoFinanceira?.inadimplente ? 'Pendências financeiras' : 'Boletos em aberto'}
            </h2>
          </header>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl bg-surface-muted p-3 flex flex-col">
              <span className="text-2xl font-semibold text-ink leading-none">
                {boletosPendentes.resumo.quantidade}
              </span>
              <span className="text-[11px] text-ink-subtle mt-1">Boletos em aberto</span>
            </div>
            <div className="rounded-xl bg-surface-muted p-3 flex flex-col">
              <span className="text-2xl font-semibold text-ink leading-none">
                R$ {boletosPendentes.resumo.totalDevido.toFixed(2)}
              </span>
              <span className="text-[11px] text-ink-subtle mt-1">Total devido</span>
            </div>
            <div className="rounded-xl bg-surface-muted p-3 flex flex-col">
              <span className="text-2xl font-semibold text-ink leading-none">
                {boletosPendentes.resumo.maiorAtraso}d
              </span>
              <span className="text-[11px] text-ink-subtle mt-1">Maior atraso</span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {boletosPendentes.boletos.map((b, i) => (
              <div key={i} className="rounded-xl border border-line/70 p-4 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <strong className="text-sm text-ink">Boleto #{b.nossoNumero}</strong>
                    <p className="text-xs text-ink-subtle mt-0.5">
                      Ref. {b.mesReferente} • venc. {new Date(b.dataVencimento).toLocaleDateString('pt-BR')} • R$ {b.valor.toFixed(2)}
                    </p>
                  </div>
                  {b.diasAtraso > 0 ? (
                    <span className="chip-danger">{b.diasAtraso}d atraso</span>
                  ) : (
                    <span className="chip-success">Em aberto</span>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="label flex items-center gap-1.5"><CreditCard size={12} /> PIX Copia e Cola</span>
                  <div className="flex gap-2">
                    <code className="flex-1 min-w-0 text-[11px] font-mono text-ink-muted bg-surface-muted rounded-lg px-3 py-2 truncate">
                      {b.pixCopiaCola}
                    </code>
                    <button
                      type="button"
                      className="btn-secondary shrink-0"
                      onClick={() => copiarPix(b.pixCopiaCola, i)}
                    >
                      {copiado === i ? <CheckCircle2 size={14} className="text-success-600" /> : <Copy size={14} />}
                      {copiado === i ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                </div>

                <a
                  href={b.linkBoleto}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-brand-700 hover:text-brand-800 hover:underline"
                >
                  <ExternalLink size={14} /> Visualizar boleto
                </a>
              </div>
            ))}
          </div>

          <div className={`mt-4 flex items-start gap-2 rounded-xl px-3.5 py-2.5 text-sm ${situacaoFinanceira?.inadimplente ? 'bg-danger-50 text-danger-700' : 'bg-success-50 text-success-700'}`}>
            {situacaoFinanceira?.inadimplente ? (
              <>
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span><strong>Atenção:</strong> Associado possui pendências financeiras. Serviço suspenso até regularização.</span>
              </>
            ) : (
              <>
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                <span><strong>Informação:</strong> Associado possui boleto disponível, mas sem atraso no momento.</span>
              </>
            )}
          </div>
        </section>
      )}

      {/* Dados do associado */}
      {associado && (
        <section className="card p-5">
          {!associado.ativo && (
            <div className="mb-4 flex items-start gap-2 rounded-xl bg-danger-50 text-danger-700 px-3.5 py-2.5 text-sm">
              <XCircle size={16} className="mt-0.5 shrink-0" />
              <span>{associado.mensagem}</span>
            </div>
          )}
          {associado.cnh?.vencida && (
            <div className="mb-4 flex items-start gap-2 rounded-xl bg-warning-50 text-warning-700 px-3.5 py-2.5 text-sm">
              <IdCard size={16} className="mt-0.5 shrink-0" />
              <span>CNH vencida em {formatarData(associado.cnh.vencimento)}. Validar condutor antes de acionar.</span>
            </div>
          )}
          {associado.spcSerasa && (
            <div className="mb-4 flex items-start gap-2 rounded-xl bg-warning-50 text-warning-700 px-3.5 py-2.5 text-sm">
              <ShieldAlert size={16} className="mt-0.5 shrink-0" />
              <span>Associado consta no SPC/Serasa.</span>
            </div>
          )}

          <header className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
                <User size={20} />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-ink truncate">{associado.nome}</h3>
                <p className="text-xs text-ink-subtle mt-0.5">Dados cadastrais do associado</p>
              </div>
            </div>
            <span className={associado.ativo ? 'chip-success' : 'chip-danger'}>{associado.status}</span>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl bg-surface-muted p-3">
              <span className="text-[11px] uppercase tracking-wider text-ink-faint">CPF</span>
              <div className="text-sm text-ink font-medium mt-0.5">{associado.cpf}</div>
            </div>
            <div className="rounded-xl bg-surface-muted p-3">
              <span className="text-[11px] uppercase tracking-wider text-ink-faint flex items-center gap-1">
                <Phone size={11} /> Telefone
              </span>
              <div className="text-sm text-ink font-medium mt-0.5">{associado.telefone || 'Não informado'}</div>
            </div>
            <div className="rounded-xl bg-surface-muted p-3">
              <span className="text-[11px] uppercase tracking-wider text-ink-faint flex items-center gap-1">
                <Mail size={11} /> E-mail
              </span>
              <div className="text-sm text-ink font-medium mt-0.5 truncate">{associado.email || 'Não informado'}</div>
            </div>
            <div className="rounded-xl bg-surface-muted p-3 md:col-span-2">
              <span className="text-[11px] uppercase tracking-wider text-ink-faint flex items-center gap-1">
                <MapPin size={11} /> Endereço
              </span>
              <div className="text-sm text-ink font-medium mt-0.5">
                {associado.endereco.logradouro}, {associado.endereco.numero}
                {associado.endereco.complemento ? ` (${associado.endereco.complemento})` : ''} — {associado.endereco.bairro},{' '}
                {associado.endereco.cidade}/{associado.endereco.estado}
              </div>
            </div>
            {associado.contrato?.dataInicio && (
              <div className="rounded-xl bg-surface-muted p-3">
                <span className="text-[11px] uppercase tracking-wider text-ink-faint">Associado desde</span>
                <div className="text-sm text-ink font-medium mt-0.5">
                  {formatarData(associado.contrato.dataInicio)}
                  <span className="text-ink-subtle font-normal"> · {tempoComoAssociado(associado.contrato.dataInicio)}</span>
                </div>
              </div>
            )}
            {associado.cobranca?.tipo && (
              <div className="rounded-xl bg-surface-muted p-3">
                <span className="text-[11px] uppercase tracking-wider text-ink-faint flex items-center gap-1">
                  <Wallet size={11} /> Cobrança
                </span>
                <div className="text-sm text-ink font-medium mt-0.5">
                  {associado.cobranca.tipo}
                  {associado.cobranca.diaVencimento && (
                    <span className="text-ink-subtle font-normal"> · venc. dia {associado.cobranca.diaVencimento}</span>
                  )}
                </div>
              </div>
            )}
            {associado.cnh && (
              <div className="rounded-xl bg-surface-muted p-3">
                <span className="text-[11px] uppercase tracking-wider text-ink-faint flex items-center gap-1">
                  <IdCard size={11} /> CNH
                </span>
                <div className="text-sm text-ink font-medium mt-0.5 flex items-center gap-2">
                  <span>{associado.cnh.numero} ({associado.cnh.categoria})</span>
                  <span className={associado.cnh.vencida ? 'chip-danger' : 'chip-neutral'}>
                    {associado.cnh.vencida ? 'vencida' : `até ${formatarData(associado.cnh.vencimento)}`}
                  </span>
                </div>
              </div>
            )}
          </div>

          <h4 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
            <Car size={16} className="text-ink-muted" />
            Veículos cadastrados ({associado.veiculos.length})
          </h4>

          {associado.veiculos.length === 0 ? (
            <p className="text-sm text-ink-subtle">Nenhum veículo retornado pela API.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {associado.veiculos.map((v) => (
                <div key={v.id} className="rounded-xl border border-line/70 p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <strong className="text-sm text-ink">{v.placa}</strong>
                    <p className="text-xs text-ink-subtle truncate mt-0.5">{v.modelo}</p>
                    {v.valorFipe ? (
                      <p className="text-xs text-ink-faint mt-0.5">FIPE: {formatarMoeda(v.valorFipe)}</p>
                    ) : null}
                  </div>
                  <span className={v.situacao === 'ATIVO' ? 'chip-success' : 'chip-neutral'}>
                    {v.situacao}
                  </span>
                </div>
              ))}
            </div>
          )}

          {associado.beneficiarios?.length > 0 && (
            <div className="mt-5">
              <h4 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
                <User size={16} className="text-ink-muted" />
                Beneficiários ({associado.beneficiarios.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {associado.beneficiarios.map((b) => (
                  <span key={b.codigo} className={b.ativo ? 'chip-success' : 'chip-neutral'}>
                    #{b.codigo} · {b.situacao}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              className={mostrarFormulario ? 'btn-secondary' : 'btn-primary'}
              onClick={() => setMostrarFormulario((p) => !p)}
            >
              {mostrarFormulario ? (
                <><XCircle size={16} /> Fechar formulário</>
              ) : (
                <><ClipboardEdit size={16} /> Abrir Atendimento</>
              )}
            </button>
          </div>
        </section>
      )}

      {mensagemAtendimento && (
        <div className="flex items-start gap-2 rounded-xl bg-success-50 text-success-700 px-3.5 py-2.5 text-sm">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>{mensagemAtendimento}</span>
        </div>
      )}
      {erroAtendimento && (
        <div className="flex items-start gap-2 rounded-xl bg-danger-50 text-danger-700 px-3.5 py-2.5 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{erroAtendimento}</span>
        </div>
      )}

      {/* Formulário de abertura */}
      {mostrarFormulario && associado && (
        <section className="card p-5">
          <header className="flex items-center gap-2 mb-1">
            <ClipboardEdit size={16} className="text-ink-muted" />
            <h2 className="text-sm font-semibold text-ink">Abertura de atendimento</h2>
          </header>
          <p className="text-xs text-ink-subtle mb-4">
            Preencha os dados da ocorrência para abrir o atendimento.
          </p>

          {!associado.ativo && (
            <div className="mb-4 flex items-start gap-2 rounded-xl bg-warning-50 text-warning-700 px-3.5 py-2.5 text-sm">
              <ShieldAlert size={16} className="mt-0.5 shrink-0" />
              <span>
                Associado não apto para liberação automática. Use este registro conforme a regra operacional da empresa.
              </span>
            </div>
          )}

          <form onSubmit={abrirAtendimento} className="flex flex-col gap-5">
            <SecaoForm titulo="Classificação" icon={Tag}>
              <CampoLabel label="Tipo do atendimento">
                <select value={tipoAtendimento} onChange={(e) => setTipoAtendimento(e.target.value)} className="input">
                  <option value="colisao">Colisão</option>
                  <option value="pane">Pane</option>
                  <option value="reboque">Reboque</option>
                  <option value="roubo_furto">Roubo/Furto</option>
                  <option value="outro">Outro</option>
                </select>
              </CampoLabel>
              <CampoLabel label="Prioridade">
                <select value={prioridadeAtendimento} onChange={(e) => setPrioridadeAtendimento(e.target.value)} className="input">
                  <option value="baixa">Baixa</option>
                  <option value="media">Média</option>
                  <option value="alta">Alta</option>
                </select>
              </CampoLabel>
              <CampoLabel label="Horário do fato">
                <input
                  type="text"
                  placeholder="ex: 14:30"
                  value={horarioFato}
                  onChange={(e) => setHorarioFato(e.target.value)}
                  className="input"
                />
              </CampoLabel>
              <CampoLabel label="Local do fato *" fullWidth>
                <input
                  type="text"
                  value={localFato}
                  onChange={(e) => setLocalFato(e.target.value)}
                  className="input"
                  required
                />
              </CampoLabel>
            </SecaoForm>

            <SecaoForm titulo="Comunicante" icon={User}>
              <CampoLabel label="Nome do comunicante">
                <input
                  type="text"
                  value={nomeComunicante}
                  onChange={(e) => setNomeComunicante(e.target.value)}
                  className="input"
                />
              </CampoLabel>
              <CampoLabel label="Contato">
                <input
                  type="text"
                  value={contatoComunicante}
                  onChange={(e) => setContatoComunicante(e.target.value)}
                  className="input"
                />
              </CampoLabel>
              <CampoLabel label="É o condutor?">
                <select value={comunicanteCondutor} onChange={(e) => setComunicanteCondutor(e.target.value)} className="input">
                  <option value="Sim">Sim</option>
                  <option value="Não">Não</option>
                </select>
              </CampoLabel>
              <CampoLabel label="Em posse da CNH?">
                <select value={cnhEmPosse} onChange={(e) => setCnhEmPosse(e.target.value)} className="input">
                  <option value="Sim">Sim</option>
                  <option value="Não">Não</option>
                </select>
              </CampoLabel>
              <CampoLabel label="Em posse do CRLV?">
                <select value={crlvEmPosse} onChange={(e) => setCrlvEmPosse(e.target.value)} className="input">
                  <option value="Sim">Sim</option>
                  <option value="Não">Não</option>
                </select>
              </CampoLabel>
            </SecaoForm>

            <SecaoForm titulo="Veículo e evento" icon={Car}>
              <CampoLabel label="Veículo" fullWidth>
                <select
                  value={veiculoSelecionadoId}
                  onChange={(e) => setVeiculoSelecionadoId(e.target.value)}
                  className="input"
                >
                  {associado.veiculos?.length > 0 ? (
                    associado.veiculos.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.placa} — {v.modelo} ({v.situacao})
                      </option>
                    ))
                  ) : (
                    <option value="">Sem veículo disponível</option>
                  )}
                </select>
              </CampoLabel>
              <CampoLabel label="Passageiros">
                <input
                  type="number"
                  value={passageiros}
                  onChange={(e) => setPassageiros(e.target.value)}
                  className="input"
                />
              </CampoLabel>
              <CampoLabel label="Envolveu outros veículos">
                <input
                  type="number"
                  value={envolveuOutrosVeiculos}
                  onChange={(e) => setEnvolveuOutrosVeiculos(e.target.value)}
                  className="input"
                />
              </CampoLabel>
              <CampoLabel label="Idosos / criança / PCD">
                <select value={idososCriancasPcd} onChange={(e) => setIdososCriancasPcd(e.target.value)} className="input">
                  <option value="Não">Não</option>
                  <option value="Sim">Sim</option>
                </select>
              </CampoLabel>
              <CampoLabel label="Quem causou">
                <select value={quemCausou} onChange={(e) => setQuemCausou(e.target.value)} className="input">
                  <option value="Associado">Associado</option>
                  <option value="Terceiro">Terceiro</option>
                  <option value="Não definido">Não definido</option>
                </select>
              </CampoLabel>
              <CampoLabel label="Álcool / medicamentos">
                <select value={consumoAlcoolMedicamentos} onChange={(e) => setConsumoAlcoolMedicamentos(e.target.value)} className="input">
                  <option value="Não">Não</option>
                  <option value="Sim">Sim</option>
                </select>
              </CampoLabel>
              <CampoLabel label="Rodas travadas">
                <select value={rodasTravadas} onChange={(e) => setRodasTravadas(e.target.value)} className="input">
                  <option value="Não">Não</option>
                  <option value="Sim">Sim</option>
                </select>
              </CampoLabel>
              <CampoLabel label="Carga / sobrepeso">
                <select value={cargaSobrepeso} onChange={(e) => setCargaSobrepeso(e.target.value)} className="input">
                  <option value="Não">Não</option>
                  <option value="Sim">Sim</option>
                </select>
              </CampoLabel>
            </SecaoForm>

            <fieldset className="border border-line/70 rounded-2xl p-5">
              <legend className="px-2 flex items-center gap-2 text-xs font-semibold text-ink-muted uppercase tracking-wider">
                <ListChecks size={13} />
                Relato e pendências
              </legend>
              <div className="flex flex-col gap-3">
                <div>
                  <label className="label">Relato do atendimento *</label>
                  <textarea
                    placeholder="Descreva o atendimento com clareza…"
                    value={relatoAtendimento}
                    onChange={(e) => setRelatoAtendimento(e.target.value)}
                    className="input min-h-[120px]"
                    rows={5}
                    required
                  />
                </div>
                <div>
                  <label className="label">Pendências / documentos aguardados</label>
                  <textarea
                    value={pendenciasAtendimento}
                    onChange={(e) => setPendenciasAtendimento(e.target.value)}
                    className="input min-h-[72px]"
                    rows={3}
                  />
                </div>
              </div>
            </fieldset>

            <div className="flex justify-end">
              <button type="submit" className="btn-primary" disabled={salvandoAtendimento}>
                {salvandoAtendimento ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {salvandoAtendimento ? 'Salvando…' : 'Confirmar abertura'}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
